import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { AdminApiService, Tournament, TournamentParticipant, TournamentMatch, RoundSetting, AdminUser } from '../../services/adminApiService';
import { getTotalRoundsFromBracketSize, getTotalRoundsFromParticipantCount, formatTournamentDateTime, defaultDateTimeLocalValue, previewTournamentBracket, assignSeedsForPreview, bracketPlayerLabel, tournamentRoundLabel, visibleRoundSettings, canEnableHardAutoStart, isSwissFormat, suggestedSwissRounds, clampSwissRounds, pairSwissRound1, computeSwissStandings, formatSwissPoints, MAX_TOURNAMENT_PLAYERS } from '../../utils/tournamentUtils';
import { getTournamentSeedPool } from '../../utils/eloUtils';
import './AdminTournamentManager.css';

interface Props {
  users: AdminUser[];
  socket?: Socket | null;
}

type View = 'list' | 'detail';

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  registration: 'Registration',
  in_progress: 'In Progress',
  complete: 'Complete',
  cancelled: 'Cancelled'
};

const MATCH_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  ready_check: 'Ready Check',
  in_progress: 'In Progress',
  complete: 'Complete',
  walkover: 'Walkover',
  double_forfeit: 'Double Forfeit',
  draw: 'Draw',
  bye: 'Win (Bye)'
};

const AdminTournamentManager: React.FC<Props> = ({ users, socket }) => {
  const [view, setView] = useState<View>('list');
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selected, setSelected] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [roundSettings, setRoundSettings] = useState<RoundSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Create form
  const [newName, setNewName] = useState('');
  const [scheduleLater, setScheduleLater] = useState(false);
  const [scheduledStartAt, setScheduledStartAt] = useState(() => defaultDateTimeLocalValue(60));
  const [newIsRated, setNewIsRated] = useState(false);
  const [newAutoStart, setNewAutoStart] = useState(false);
  const [newFormat, setNewFormat] = useState<'single_elim' | 'swiss'>('single_elim');

  // Player search
  const [playerSearch, setPlayerSearch] = useState('');
  const [playerDropdown, setPlayerDropdown] = useState(false);

  // Round time control editor
  const [editingRound, setEditingRound] = useState<number | null>(null);
  const [roundMinutes, setRoundMinutes] = useState('');
  const [roundIncrement, setRoundIncrement] = useState('0');

  // Force-resolve modal
  const [forceMatchId, setForceMatchId] = useState<number | null>(null);
  const [forceWinnerId, setForceWinnerId] = useState<string>('');
  const [actionBusy, setActionBusy] = useState(false);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    const result = await AdminApiService.getTournaments();
    if (!result.error) setTournaments(result.tournaments);
    setLoading(false);
  }, []);

  const refreshTournamentsListQuiet = useCallback(async () => {
    const result = await AdminApiService.getTournaments();
    if (!result.error) setTournaments(result.tournaments);
  }, []);

  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  const loadDetail = useCallback(async (id: number, opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    const result = await AdminApiService.getTournamentDetail(id);
    if (!result.error && result.tournament) {
      setSelected(result.tournament);
      setParticipants(result.participants);
      setMatches(result.matches);
      setRoundSettings(result.roundSettings);
    } else if (!opts?.silent) {
      showToast(result.error || 'Failed to load tournament', false);
    }
    if (!opts?.silent) setLoading(false);
  }, []);

  // Live bracket: same tournament:updated broadcast as player UI — refetch without manual refresh
  useEffect(() => {
    if (!socket) return;
    const onUpdated = ({ tournamentId }: { tournamentId: number }) => {
      void refreshTournamentsListQuiet();
      if (selected?.id === tournamentId && view === 'detail') {
        loadDetail(tournamentId, { silent: true });
      }
    };
    socket.on('tournament:updated', onUpdated);
    return () => { socket.off('tournament:updated', onUpdated); };
  }, [socket, selected?.id, view, refreshTournamentsListQuiet, loadDetail]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    let scheduledIso: string | undefined;
    if (scheduleLater) {
      if (!scheduledStartAt) {
        showToast('Choose a scheduled start date and time', false);
        return;
      }
      const startMs = new Date(scheduledStartAt).getTime();
      if (Number.isNaN(startMs) || startMs <= Date.now()) {
        showToast('Scheduled start must be in the future', false);
        return;
      }
      scheduledIso = new Date(startMs).toISOString();
    } else if (newAutoStart) {
      showToast('Hard auto-start requires a scheduled start time', false);
      return;
    }
    const result = await AdminApiService.createTournament(newName.trim(), scheduledIso, newIsRated, newAutoStart, newFormat);
    if (result.error) { showToast(result.error, false); return; }
    showToast(scheduleLater ? 'Scheduled tournament created' : 'Tournament created');
    setNewName('');
    setScheduleLater(false);
    setScheduledStartAt(defaultDateTimeLocalValue(60));
    setNewIsRated(false);
    setNewAutoStart(false);
    setNewFormat('single_elim');
    await loadTournaments();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this tournament? This cannot be undone.')) return;
    const result = await AdminApiService.deleteTournament(id);
    if (result.error) { showToast(result.error, false); return; }
    showToast('Tournament deleted');
    if (selected?.id === id) { setView('list'); setSelected(null); }
    await loadTournaments();
  };

  const handleOpen = async (t: Tournament) => {
    setView('detail');
    await loadDetail(t.id);
  };

  const handleAddPlayer = async (user: AdminUser) => {
    if (!selected) return;
    if (participants.length >= MAX_TOURNAMENT_PLAYERS) {
      showToast(`Tournaments are limited to ${MAX_TOURNAMENT_PLAYERS} players`, false);
      return;
    }
    const result = await AdminApiService.addParticipant(selected.id, user.id);
    if (result.error) { showToast(result.error, false); return; }
    setParticipants(result.participants);
    setPlayerSearch('');
    setPlayerDropdown(false);
    showToast(`${user.username} added`);
  };

  const handleRemovePlayer = async (userId: number, username: string) => {
    if (!selected) return;
    if (!window.confirm(`Remove ${username} from the tournament?`)) return;
    const result = await AdminApiService.removeParticipant(selected.id, userId);
    if (result.error) { showToast(result.error, false); return; }
    setParticipants(result.participants);
    showToast(`${username} removed`);
  };

  const handleToggleRegistration = async () => {
    if (!selected) return;
    const newOpen = !selected.registration_open;
    const result = await AdminApiService.setRegistrationOpen(selected.id, !!newOpen);
    if (result.error) { showToast(result.error, false); return; }
    setSelected(prev => prev ? { ...prev, registration_open: newOpen ? 1 : 0 } : prev);
    showToast(`Registration ${newOpen ? 'opened' : 'closed'}`);
  };

  const handleToggleFormat = async (format: 'single_elim' | 'swiss') => {
    if (!selected || selected.format === format) return;
    const result = await AdminApiService.setTournamentFormat(selected.id, format);
    if (result.error) { showToast(result.error, false); return; }
    if (result.tournament) setSelected(result.tournament);
    showToast(format === 'swiss' ? 'Format: Swiss' : 'Format: Bracket');
    await refreshTournamentsListQuiet();
  };

  const handleSaveSwissRounds = async (rounds: number) => {
    if (!selected) return;
    const result = await AdminApiService.setSwissRounds(selected.id, rounds);
    if (result.error) { showToast(result.error, false); return; }
    if (result.tournament) setSelected(result.tournament);
    showToast(`Swiss event set to ${result.tournament?.total_rounds} rounds`);
  };

  const handleToggleRated = async () => {
    if (!selected) return;
    const nextRated = !(selected.is_rated === 1 || selected.is_rated === true);
    const result = await AdminApiService.setTournamentRated(selected.id, nextRated);
    if (result.error) { showToast(result.error, false); return; }
    setSelected(prev => prev ? { ...prev, is_rated: nextRated ? 1 : 0 } : prev);
    showToast(nextRated ? 'Tournament marked as rated' : 'Tournament marked as unrated');
    await refreshTournamentsListQuiet();
  };

  const handleToggleAutoStart = async () => {
    if (!selected) return;
    const nextAuto = !(selected.auto_start === 1 || selected.auto_start === true);
    const result = await AdminApiService.setTournamentAutoStart(selected.id, nextAuto);
    if (result.error) { showToast(result.error, false); return; }
    setSelected(prev => prev ? { ...prev, auto_start: nextAuto ? 1 : 0 } : prev);
    showToast(nextAuto ? 'Hard auto-start enabled' : 'Manual start (default)');
    await refreshTournamentsListQuiet();
  };

  const handleSaveRoundTimeControl = async (round: number) => {
    if (!selected || !roundMinutes) return;
    const mins = parseInt(roundMinutes);
    const inc = parseInt(roundIncrement) || 0;
    if (isNaN(mins) || mins < 1) { showToast('Minutes must be at least 1', false); return; }
    if (selected.is_rated === 1 || selected.is_rated === true) {
      const ok = window.confirm('This tournament is rated. Changing a round clock can change whether games count as Fast or Standard. Save anyway?');
      if (!ok) return;
    }
    const result = await AdminApiService.setRoundTimeControl(selected.id, round, mins, inc);
    if (result.error) { showToast(result.error, false); return; }
    setRoundSettings(prev => {
      const filtered = prev.filter(r => r.round !== round);
      return [...filtered, { id: 0, tournament_id: selected.id, round, minutes_per_player: mins, increment_seconds: inc }];
    });
    setEditingRound(null);
    showToast(`Round ${round} time control saved`);
  };

  const handleStartTournament = async () => {
    if (!selected || actionBusy) return;
    if (!window.confirm(`Start "${selected.name}"? This locks registration and generates ${isSwissFormat(selected) ? 'Round 1 pairings' : 'the bracket'}. You will still need to open Round 1 to send ready checks.`)) return;
    setActionBusy(true);
    try {
      const result = await AdminApiService.startTournament(selected.id);
      if (result.error) { showToast(result.error, false); return; }
      showToast('Tournament started — open Round 1 when players are ready.');
      await loadDetail(selected.id);
    } finally {
      setActionBusy(false);
    }
  };

  const handleStartAndOpenRound1 = async () => {
    if (!selected || actionBusy) return;
    if (!window.confirm(`Start "${selected.name}" and open Round 1 now? This locks registration, generates ${isSwissFormat(selected) ? 'Round 1 pairings' : 'the bracket'}, and sends ready checks.`)) return;
    setActionBusy(true);
    try {
      const started = await AdminApiService.startTournament(selected.id);
      if (started.error) { showToast(started.error, false); return; }
      const opened = await AdminApiService.openRound(selected.id, 1);
      if (opened.error) {
        showToast(`Started, but Round 1 did not open: ${opened.error}`, false);
        await loadDetail(selected.id);
        return;
      }
      showToast('Tournament started and Round 1 opened — ready checks sent.');
      await loadDetail(selected.id);
    } finally {
      setActionBusy(false);
    }
  };

  const handleMoveSeed = async (userId: number, direction: -1 | 1) => {
    if (!selected) return;
    const ordered = assignSeedsForPreview(participants).sort((a, b) => (a.seed || 0) - (b.seed || 0));
    const idx = ordered.findIndex(p => p.user_id === userId);
    const swap = idx + direction;
    if (idx < 0 || swap < 0 || swap >= ordered.length) return;
    const tmp = ordered[idx];
    ordered[idx] = ordered[swap];
    ordered[swap] = tmp;
    const seeds = ordered.map((p, i) => ({ userId: p.user_id, seed: i + 1 }));
    const result = await AdminApiService.setParticipantSeeds(selected.id, seeds);
    if (result.error) { showToast(result.error, false); return; }
    setParticipants(result.participants);
  };

  const handleOpenRound = async (round: number) => {
    if (!selected || actionBusy) return;
    if (!window.confirm(`Open Round ${round}? This sends ready checks to all players.`)) return;
    setActionBusy(true);
    try {
      const result = await AdminApiService.openRound(selected.id, round);
      if (result.error) { showToast(result.error, false); return; }
      showToast(`Round ${round} opened — ready checks sent!`);
      await loadDetail(selected.id);
    } finally {
      setActionBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!selected) return;
    if (!window.confirm(`Cancel "${selected.name}"? Players will be notified.`)) return;
    const result = await AdminApiService.cancelTournament(selected.id);
    if (result.error) { showToast(result.error, false); return; }
    showToast('Tournament cancelled');
    await loadDetail(selected.id);
    await loadTournaments();
  };

  const handleForceResolve = async () => {
    if (forceMatchId === null) return;
    const outcome = forceWinnerId === 'draw' ? 'draw' : undefined;
    const wId = forceWinnerId && forceWinnerId !== 'draw' ? parseInt(forceWinnerId) : null;
    const result = await AdminApiService.forceResolveMatch(forceMatchId, wId, outcome);
    if (result.error) { showToast(result.error, false); return; }
    showToast('Match force-resolved');
    setForceMatchId(null);
    setForceWinnerId('');
    if (selected) await loadDetail(selected.id);
  };

  // Derived helpers
  const isSetupPhase = selected?.status === 'scheduled' || selected?.status === 'registration';
  const swiss = isSwissFormat(selected);
  const registrationTotalRounds = isSetupPhase
    ? (swiss
        ? (participants.length >= 2
            ? clampSwissRounds(participants.length, selected?.total_rounds || suggestedSwissRounds(participants.length))
            : 0)
        : getTotalRoundsFromParticipantCount(participants.length))
    : 0;
  const totalRounds = selected
    ? (isSetupPhase
        ? registrationTotalRounds
        : (swiss ? (selected.total_rounds || 0) : getTotalRoundsFromBracketSize(selected.bracket_size || 2)))
    : 0;
  const allRoundsConfigured = isSetupPhase
    ? registrationTotalRounds > 0 &&
      Array.from({ length: registrationTotalRounds }, (_, i) => i + 1)
        .every(r => roundSettings.some(s => s.round === r))
    : true;

  const currentRound = selected?.current_round || 0;
  const currentRoundMatches = matches.filter(m => m.round === currentRound);
  const isCurrentRoundComplete = currentRoundMatches.length > 0 &&
    currentRoundMatches.every(m => ['complete', 'walkover', 'double_forfeit', 'bye', 'draw'].includes(m.status));
  const nextRound = currentRound + 1;
  const nextRoundTimeSet = roundSettings.some(s => s.round === nextRound);
  const canGoNextRound = isCurrentRoundComplete && nextRoundTimeSet && nextRound <= totalRounds;
  const currentRoundHasUnstarted = currentRoundMatches.some(m =>
    m.status === 'pending' || m.status === 'ready_check'
  );

  const bracketPreview = !swiss && isSetupPhase && participants.length >= 2
    ? previewTournamentBracket(participants, getTournamentSeedPool(roundSettings))
    : null;
  const swissPreview = swiss && isSetupPhase && participants.length >= 2
    ? pairSwissRound1(assignSeedsForPreview(participants, getTournamentSeedPool(roundSettings)))
    : null;
  const swissStandings = swiss && !isSetupPhase
    ? computeSwissStandings(participants, matches)
    : [];
  const autoStartReady = canEnableHardAutoStart(
    participants.length,
    roundSettings,
    selected?.scheduled_start_at,
    { format: selected?.format, plannedRounds: selected?.total_rounds }
  );
  const displayedRoundSettings = selected
    ? (isSetupPhase ? roundSettings : visibleRoundSettings(roundSettings, totalRounds))
    : [];
  const setupSeedOrder = isSetupPhase ? assignSeedsForPreview(participants) : [];

  const filteredUsers = users.filter(u =>
    playerSearch.length >= 2 &&
    u.username.toLowerCase().includes(playerSearch.toLowerCase()) &&
    !participants.some(p => p.user_id === u.id)
  ).slice(0, 8);

  const roundsArray = totalRounds > 0
    ? Array.from({ length: totalRounds }, (_, i) => i + 1)
    : [];
  const roundsNewestFirst = [...roundsArray].reverse();

  const renderRoundStartActions = (round: number) => {
    if (!selected || selected.status !== 'in_progress') return null;
    if (currentRound === 0 && round === 1) {
      return (
        <div className="atm-go-row">
          <button
            className="atm-btn atm-btn-go"
            disabled={actionBusy}
            onClick={() => handleOpenRound(1)}
          >
            Start Round 1
          </button>
        </div>
      );
    }
    if (round === nextRound && nextRound <= totalRounds) {
      return (
        <div className="atm-go-row">
          {!isCurrentRoundComplete && (
            <span className="atm-warn-inline">Round {currentRound} still in progress</span>
          )}
          {isCurrentRoundComplete && !nextRoundTimeSet && (
            <span className="atm-warn-inline">Set Round {nextRound} time control to enable</span>
          )}
          <button
            className="atm-btn atm-btn-go"
            disabled={actionBusy || !canGoNextRound}
            onClick={() => handleOpenRound(nextRound)}
          >
            Start Round {nextRound}
          </button>
        </div>
      );
    }
    if (round === currentRound && currentRoundHasUnstarted) {
      return (
        <div className="atm-go-row">
          <button
            className="atm-btn atm-btn-secondary"
            disabled={actionBusy}
            onClick={() => handleOpenRound(currentRound)}
          >
            Re-open Round {currentRound}
          </button>
        </div>
      );
    }
    return null;
  };

  if (view === 'list') {
    return (
      <div className="atm-container">
        {toast && (
          <div className={`atm-toast ${toast.ok ? 'atm-toast-ok' : 'atm-toast-err'}`}>{toast.msg}</div>
        )}

        <div className="atm-header">
          <h2>Tournament Manager</h2>
          <div className="atm-create-row">
            <input
              className="atm-input"
              placeholder="Tournament name..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
            <button className="atm-btn atm-btn-primary" onClick={handleCreate} disabled={!newName.trim()}>
              Create Tournament
            </button>
          </div>
          <div className="atm-create-schedule">
            <label className="atm-checkbox-row">
              <input
                type="checkbox"
                checked={scheduleLater}
                onChange={e => {
                  setScheduleLater(e.target.checked);
                  if (!e.target.checked) setNewAutoStart(false);
                }}
              />
              Schedule for a future date &amp; time
            </label>
            {scheduleLater && (
              <input
                className="atm-input atm-input-datetime"
                type="datetime-local"
                value={scheduledStartAt}
                min={defaultDateTimeLocalValue(1)}
                onChange={e => setScheduledStartAt(e.target.value)}
              />
            )}
            {scheduleLater && (
              <label className="atm-checkbox-row">
                <input
                  type="checkbox"
                  checked={newAutoStart}
                  onChange={e => setNewAutoStart(e.target.checked)}
                />
                Hard auto-start at scheduled time (starts tournament + Round 1)
              </label>
            )}
            <label className="atm-checkbox-row">
              <input
                type="checkbox"
                checked={newIsRated}
                onChange={e => setNewIsRated(e.target.checked)}
              />
              Rated tournament (affects Elo when ratings are active)
            </label>
            <div className="atm-format-toggle">
              <span>Format</span>
              <button
                type="button"
                className={`atm-btn atm-btn-sm ${newFormat === 'single_elim' ? 'atm-btn-primary' : 'atm-btn-secondary'}`}
                onClick={() => setNewFormat('single_elim')}
              >
                Bracket
              </button>
              <button
                type="button"
                className={`atm-btn atm-btn-sm ${newFormat === 'swiss' ? 'atm-btn-primary' : 'atm-btn-secondary'}`}
                onClick={() => setNewFormat('swiss')}
              >
                Swiss
              </button>
            </div>
          </div>
        </div>

        {loading && <div className="atm-loading">Loading...</div>}

        {tournaments.length === 0 && !loading && (
          <div className="atm-empty">No tournaments yet. Create one above.</div>
        )}

        <div className="atm-list">
          {tournaments.map(t => (
            <div key={t.id} className="atm-card">
              <div className="atm-card-info">
                <span className="atm-card-name">{t.name}</span>
                <span className={`atm-badge atm-badge-${t.status}`}>{STATUS_LABELS[t.status] || t.status}</span>
                <span className="atm-badge">{t.format === 'swiss' ? 'Swiss' : 'Bracket'}</span>
                {(t.is_rated === 1 || t.is_rated === true) && (
                  <span className="atm-badge atm-badge-rated">Rated</span>
                )}
                {(t.auto_start === 1 || t.auto_start === true) && (
                  <span className="atm-badge atm-badge-autostart">Auto-start</span>
                )}
                <span className="atm-card-meta">{t.player_count || 0} players</span>
                {t.scheduled_start_at && (t.status === 'scheduled' || t.status === 'registration') && (
                  <span className="atm-card-meta">Starts {formatTournamentDateTime(t.scheduled_start_at)}</span>
                )}
                {t.status === 'in_progress' && (
                  <span className="atm-card-meta">{tournamentRoundLabel(t.current_round, t.total_rounds, t.format)}</span>
                )}
                {t.last_auto_start_error && (
                  <span className="atm-card-meta atm-autostart-error">Auto-start: {t.last_auto_start_error}</span>
                )}
              </div>
              <div className="atm-card-actions">
                <button className="atm-btn atm-btn-secondary" onClick={() => handleOpen(t)}>Manage</button>
                {t.status !== 'in_progress' && (
                  <button className="atm-btn atm-btn-danger" onClick={() => handleDelete(t.id)}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Detail view
  return (
    <div className={`atm-container${selected && !isSetupPhase ? ' atm-container-live' : ''}`}>
      {toast && (
        <div className={`atm-toast ${toast.ok ? 'atm-toast-ok' : 'atm-toast-err'}`}>{toast.msg}</div>
      )}

      <div className="atm-header">
        <button className="atm-btn atm-btn-back" onClick={() => setView('list')}>← All Tournaments</button>
        <h2>{selected?.name}</h2>
        <span className={`atm-badge atm-badge-${selected?.status}`}>{STATUS_LABELS[selected?.status || ''] || selected?.status}</span>
        {selected && (
          <span className="atm-badge">{isSwissFormat(selected) ? 'Swiss' : 'Bracket'}</span>
        )}
        {selected && (selected.is_rated === 1 || selected.is_rated === true) && (
          <span className="atm-badge atm-badge-rated">Rated</span>
        )}
        {selected && (selected.auto_start === 1 || selected.auto_start === true) && (
          <span className="atm-badge atm-badge-autostart">Auto-start</span>
        )}
        {selected && (selected.status === 'scheduled' || selected.status === 'registration' || selected.status === 'in_progress') && (
          <button className="atm-btn atm-btn-danger atm-btn-sm" onClick={handleCancel}>Cancel Tournament</button>
        )}
      </div>

      {loading && <div className="atm-loading">Loading...</div>}

      {selected && (
        <div className={`atm-detail${isSetupPhase ? '' : ' atm-detail-live'}`}>
          <div className="atm-col atm-col-meta">

          {/* ── Scheduled Info ── */}
          {selected.scheduled_start_at && isSetupPhase && (
            <section className="atm-section">
              <h3>Scheduled Start</h3>
              <div className="atm-info">
                Registration is open now. At{' '}
                <strong>{formatTournamentDateTime(selected.scheduled_start_at) || 'the scheduled time'}</strong>
                {(selected.auto_start === 1 || selected.auto_start === true)
                  ? ', registration closes and the tournament + Round 1 start automatically.'
                  : ', registration closes. You still start the tournament and open each round manually.'}
              </div>
            </section>
          )}

          {/* ── Registration Controls ── */}
          {isSetupPhase && (
            <section className="atm-section">
              <h3>Registration</h3>
              <div className="atm-row">
                <span>{selected.registration_open ? 'Open — members can sign up' : 'Closed — admin only'}</span>
                <button className="atm-btn atm-btn-secondary" onClick={handleToggleRegistration}>
                  {selected.registration_open ? 'Close Registration' : 'Open Registration'}
                </button>
              </div>
            </section>
          )}

          {/* ── Format ── */}
          <section className="atm-section">
            <h3>Format</h3>
            <div className="atm-row">
              <span>
                {swiss
                  ? 'Swiss — everyone plays every round; draws count as ½–½; standings decide the winner'
                  : 'Bracket — single-elim knockout; draws go to a 1+0 playoff'}
              </span>
              {isSetupPhase ? (
                <div className="atm-format-toggle">
                  <button
                    className={`atm-btn atm-btn-sm ${!swiss ? 'atm-btn-primary' : 'atm-btn-secondary'}`}
                    onClick={() => handleToggleFormat('single_elim')}
                  >
                    Bracket
                  </button>
                  <button
                    className={`atm-btn atm-btn-sm ${swiss ? 'atm-btn-primary' : 'atm-btn-secondary'}`}
                    onClick={() => handleToggleFormat('swiss')}
                  >
                    Swiss
                  </button>
                </div>
              ) : (
                <span className="atm-card-meta">Locked after start</span>
              )}
            </div>
          </section>

          {/* ── Rated Setting ── */}
          <section className="atm-section">
            <h3>Rating</h3>
            <div className="atm-row">
              <span>
                {(selected.is_rated === 1 || selected.is_rated === true)
                  ? 'Rated — players will see a notice before signing up'
                  : 'Unrated — default'}
              </span>
              {isSetupPhase ? (
                <button className="atm-btn atm-btn-secondary" onClick={handleToggleRated}>
                  {(selected.is_rated === 1 || selected.is_rated === true) ? 'Make Unrated' : 'Make Rated'}
                </button>
              ) : (
                <span className="atm-card-meta">Locked after start</span>
              )}
            </div>
          </section>

          {/* ── Auto-start Setting ── */}
          <section className="atm-section">
            <h3>Start Mode</h3>
            <div className="atm-row">
              <span>
                {(selected.auto_start === 1 || selected.auto_start === true)
                  ? 'Hard auto-start — at scheduled time: close registration, start tournament, open Round 1'
                  : 'Manual — default. Admin starts the tournament and opens each round'}
              </span>
              {isSetupPhase ? (
                <button
                  className="atm-btn atm-btn-secondary"
                  onClick={handleToggleAutoStart}
                  disabled={!(selected.auto_start === 1 || selected.auto_start === true) && !autoStartReady.ok}
                  title={
                    (selected.auto_start === 1 || selected.auto_start === true)
                      ? undefined
                      : (autoStartReady.reason || undefined)
                  }
                >
                  {(selected.auto_start === 1 || selected.auto_start === true) ? 'Use Manual Start' : 'Enable Hard Auto-Start'}
                </button>
              ) : (
                <span className="atm-card-meta">Locked after start</span>
              )}
            </div>
            {isSetupPhase && !(selected.auto_start === 1 || selected.auto_start === true) && !autoStartReady.ok && (
              <div className="atm-info">{autoStartReady.reason}</div>
            )}
            {selected.last_auto_start_error && (
              <div className="atm-warn">Last auto-start error: {selected.last_auto_start_error}</div>
            )}
          </section>

          {/* ── Players ── */}
          <section className="atm-section">
            <h3>Players ({participants.length} / {MAX_TOURNAMENT_PLAYERS})</h3>

            {isSetupPhase && participants.length >= MAX_TOURNAMENT_PLAYERS && (
              <div className="atm-warn">Field is full ({MAX_TOURNAMENT_PLAYERS} players).</div>
            )}
            {isSetupPhase && participants.length < MAX_TOURNAMENT_PLAYERS && (
              <div className="atm-player-search" style={{ position: 'relative' }}>
                <input
                  className="atm-input"
                  placeholder="Search players to add..."
                  value={playerSearch}
                  onChange={e => { setPlayerSearch(e.target.value); setPlayerDropdown(true); }}
                  onFocus={() => setPlayerDropdown(true)}
                  onBlur={() => setTimeout(() => setPlayerDropdown(false), 200)}
                />
                {playerDropdown && filteredUsers.length > 0 && (
                  <div className="atm-dropdown">
                    {filteredUsers.map(u => (
                      <button key={u.id} className="atm-dropdown-item" onMouseDown={() => handleAddPlayer(u)}>
                        {u.username} <span className="atm-elo">Std {(u as any).elo_standard || (u as any).elo_rating || 1200} · Fast {(u as any).elo_fast || 1200}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="atm-player-list">
              {participants.length === 0 && <span className="atm-empty">No players yet.</span>}
              {(isSetupPhase
                ? [...setupSeedOrder].sort((a, b) => (a.seed || 0) - (b.seed || 0))
                : participants
              ).map((p, i, arr) => (
                <div key={p.user_id} className="atm-player-row">
                  <span className="atm-seed">#{p.seed || i + 1}</span>
                  <span className="atm-pname">{p.username}</span>
                  <span className="atm-elo">Std {p.elo_standard || p.elo_rating || 1200} · Fast {p.elo_fast || 1200}</span>
                  <span className={`atm-pstatus atm-pstatus-${p.status}`}>{p.status}</span>
                  {isSetupPhase && arr.length >= 2 && (
                    <span className="atm-seed-move">
                      <button
                        className="atm-btn atm-btn-secondary atm-btn-sm"
                        disabled={i === 0}
                        onClick={() => handleMoveSeed(p.user_id, -1)}
                        title="Move seed up"
                      >↑</button>
                      <button
                        className="atm-btn atm-btn-secondary atm-btn-sm"
                        disabled={i === arr.length - 1}
                        onClick={() => handleMoveSeed(p.user_id, 1)}
                        title="Move seed down"
                      >↓</button>
                    </span>
                  )}
                  {isSetupPhase && (
                    <button className="atm-btn atm-btn-danger atm-btn-sm" onClick={() => handleRemovePlayer(p.user_id, p.username)}>✕</button>
                  )}
                </div>
              ))}
            </div>
            {isSetupPhase && participants.length >= 2 && (
              <div className="atm-info">Seeds shown are Elo order until you move someone. After you reorder, Start keeps your seeds.</div>
            )}
          </section>

          {/* ── Projected pairings ── */}
          {isSetupPhase && (
            <section className="atm-section">
              <h3>{swiss ? 'Projected Round 1' : 'Projected Bracket'}</h3>
              {swiss && participants.length >= 2 && (
                <div className="atm-row" style={{ marginBottom: 12 }}>
                  <span>Swiss rounds (suggested {suggestedSwissRounds(participants.length)})</span>
                  <select
                    className="atm-input atm-input-sm"
                    value={registrationTotalRounds}
                    onChange={e => handleSaveSwissRounds(parseInt(e.target.value, 10))}
                  >
                    {Array.from(
                      { length: Math.max(1, clampSwissRounds(participants.length, 99)) },
                      (_, i) => i + 1
                    ).map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              )}
              {!bracketPreview && !swissPreview && (
                <div className="atm-info">Add at least 2 players to preview Round 1 pairings and byes.</div>
              )}
              {swissPreview && (
                <>
                  <div className="atm-info">
                    {participants.length} players · {registrationTotalRounds} Swiss rounds
                    {swissPreview.bye && ` · Round 1 bye: #${swissPreview.bye.seed} ${swissPreview.bye.username}`}
                    . Top half vs bottom half by seed. Draws stay as ½–½.
                  </div>
                  <div className="atm-bracket-preview">
                    {swissPreview.pairings.map((pair, i) => (
                      <div key={i} className="atm-preview-match">
                        <span className="atm-preview-seed">#{pair.player1?.seed || '—'}</span>
                        <span className="atm-preview-name">{pair.player1?.username}</span>
                        <span className="atm-vs">vs</span>
                        <span className="atm-preview-seed">#{pair.player2?.seed || '—'}</span>
                        <span className="atm-preview-name">{pair.player2?.username}</span>
                      </div>
                    ))}
                    {swissPreview.bye && (
                      <div className="atm-preview-match atm-preview-bye">
                        <span className="atm-preview-seed">#{swissPreview.bye.seed}</span>
                        <span className="atm-preview-name">{swissPreview.bye.username}</span>
                        <span className="atm-vs">vs</span>
                        <span className="atm-preview-name">Bye</span>
                        <span className="atm-preview-bye-tag">Bye</span>
                      </div>
                    )}
                  </div>
                </>
              )}
              {bracketPreview && (
                <>
                  <div className="atm-info">
                    {participants.length} players → {bracketPreview.bracketSize}-slot bracket, {bracketPreview.totalRounds} rounds
                    {bracketPreview.byeCount > 0 && (
                      <>
                        {' · '}
                        {bracketPreview.byeCount} first-round bye{bracketPreview.byeCount !== 1 ? 's' : ''}
                        {' '}(highest seed{bracketPreview.byeCount !== 1 ? 's' : ''})
                      </>
                    )}
                    . Seeds are by {getTournamentSeedPool(roundSettings) === 'fast' ? 'Fast' : 'Standard'} Elo unless you set them manually. This is what Start will generate.
                  </div>
                  <div className="atm-bracket-preview">
                    {bracketPreview.pairings.map((pair, i) => {
                      const p1 = pair.player1;
                      const p2 = pair.player2;
                      const isBye = (p1 && !p2) || (!p1 && p2);
                      return (
                        <div key={i} className={`atm-preview-match ${isBye ? 'atm-preview-bye' : ''}`}>
                          <span className="atm-preview-seed">#{p1?.seed || '—'}</span>
                          <span className="atm-preview-name">{p1?.username || 'Bye'}</span>
                          <span className="atm-vs">vs</span>
                          <span className="atm-preview-seed">#{p2?.seed || (isBye ? '' : '—')}</span>
                          <span className="atm-preview-name">{p2?.username || (isBye ? 'Bye' : 'TBD')}</span>
                          {isBye && <span className="atm-preview-bye-tag">Bye</span>}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          )}

          {/* ── Round Time Controls ── */}
          <section className="atm-section">
            <h3>Round Time Controls</h3>
            {isSetupPhase && participants.length < 2 && (
              <div className="atm-info">
                Add at least 2 players to configure round time controls.
              </div>
            )}
            {isSetupPhase && participants.length >= 2 && (
              <div className="atm-round-preview">
                {Array.from({ length: registrationTotalRounds }, (_, i) => i + 1).map(r => {
                    const setting = roundSettings.find(s => s.round === r);
                    const isEditing = editingRound === r;
                    return (
                      <div key={r} className="atm-round-row">
                        <span className="atm-round-label">Round {r}</span>
                        {setting && !isEditing && (
                          <span className="atm-tc">{setting.minutes_per_player}min +{setting.increment_seconds}s</span>
                        )}
                        {!setting && !isEditing && (
                          <span className="atm-tc-missing">Not set</span>
                        )}
                        {isEditing ? (
                          <div className="atm-tc-edit">
                            <input className="atm-input atm-input-sm" type="number" min="1" max="60" placeholder="min" value={roundMinutes} onChange={e => setRoundMinutes(e.target.value)} />
                            <span>+</span>
                            <input className="atm-input atm-input-sm" type="number" min="0" max="60" placeholder="inc" value={roundIncrement} onChange={e => setRoundIncrement(e.target.value)} />
                            <span>s</span>
                            <button className="atm-btn atm-btn-primary atm-btn-sm" onClick={() => handleSaveRoundTimeControl(r)}>Save</button>
                            <button className="atm-btn atm-btn-secondary atm-btn-sm" onClick={() => setEditingRound(null)}>Cancel</button>
                          </div>
                        ) : (
                          <button className="atm-btn atm-btn-secondary atm-btn-sm" onClick={() => { setEditingRound(r); setRoundMinutes(setting?.minutes_per_player?.toString() || ''); setRoundIncrement(setting?.increment_seconds?.toString() || '0'); }}>
                            {setting ? 'Edit' : 'Set'}
                          </button>
                        )}
                      </div>
                    );
                })}
              </div>
            )}
            {!isSetupPhase && displayedRoundSettings.map(s => (
              <div key={s.round} className="atm-round-row">
                <span className="atm-round-label">Round {s.round}</span>
                <span className="atm-tc">{s.minutes_per_player}min +{s.increment_seconds}s</span>
                {selected.current_round < s.round && (
                  <>
                    {editingRound === s.round ? (
                      <div className="atm-tc-edit">
                        <input className="atm-input atm-input-sm" type="number" min="1" placeholder="min" value={roundMinutes} onChange={e => setRoundMinutes(e.target.value)} />
                        <span>+</span>
                        <input className="atm-input atm-input-sm" type="number" min="0" placeholder="inc" value={roundIncrement} onChange={e => setRoundIncrement(e.target.value)} />
                        <span>s</span>
                        <button className="atm-btn atm-btn-primary atm-btn-sm" onClick={() => handleSaveRoundTimeControl(s.round)}>Save</button>
                        <button className="atm-btn atm-btn-secondary atm-btn-sm" onClick={() => setEditingRound(null)}>Cancel</button>
                      </div>
                    ) : (
                      <button className="atm-btn atm-btn-secondary atm-btn-sm" onClick={() => { setEditingRound(s.round); setRoundMinutes(s.minutes_per_player.toString()); setRoundIncrement(s.increment_seconds.toString()); }}>Edit</button>
                    )}
                  </>
                )}
              </div>
            ))}
          </section>

          {/* ── Start Tournament ── */}
          {selected.status === 'registration' && (
            <section className="atm-section">
              <h3>Start Tournament</h3>
              {participants.length < 2 && <div className="atm-warn">Need at least 2 players.</div>}
              {participants.length >= 2 && !allRoundsConfigured && (
                <div className="atm-warn">All round time controls must be set before starting.</div>
              )}
              <div className="atm-go-row">
                <button
                  className="atm-btn atm-btn-go"
                  disabled={actionBusy || participants.length < 2 || !allRoundsConfigured}
                  onClick={handleStartAndOpenRound1}
                >
                  Start and open Round 1
                </button>
                <button
                  className="atm-btn atm-btn-secondary"
                  disabled={actionBusy || participants.length < 2 || !allRoundsConfigured}
                  onClick={handleStartTournament}
                >
                  Start only (open Round 1 later)
                </button>
              </div>
              <div className="atm-info">
                “Start and open Round 1” generates {swiss ? 'Round 1 pairings' : 'the bracket'} and immediately sends ready checks.
                “Start only” lets you review {swiss ? 'the pairings' : 'the bracket'} first.
              </div>
            </section>
          )}

          </div>

          {!isSetupPhase && (
            <div className="atm-col atm-col-standings">
              <section className="atm-section">
                <h3>{swiss ? 'Standings' : 'Players'}</h3>
                {selected.status === 'complete' && (
                  <div className="atm-complete-banner">
                    Tournament complete
                    {swiss
                      ? (swissStandings[0]?.username && (
                          <span> — <strong>{swissStandings[0].username}</strong></span>
                        ))
                      : (matches.find(m => m.round === selected.total_rounds && m.winner_username) && (
                          <span> — <strong>{matches.find(m => m.round === selected.total_rounds)?.winner_username}</strong></span>
                        ))}
                  </div>
                )}
                {swiss && swissStandings.length > 0 ? (
                  <table className="atm-standings">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Player</th>
                        <th>Pts</th>
                        <th>Buchholz</th>
                      </tr>
                    </thead>
                    <tbody>
                      {swissStandings.map((row, i) => (
                        <tr key={row.user_id} className={row.source.status === 'winner' ? 'atm-standings-leader' : ''}>
                          <td>{i + 1}</td>
                          <td>{row.username}{row.seed ? ` (#${row.seed})` : ''}</td>
                          <td>{formatSwissPoints(row.points)}</td>
                          <td>{formatSwissPoints(row.buchholz)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="atm-player-list">
                    {participants.map((p, i) => (
                      <div key={p.user_id} className="atm-player-row">
                        <span className="atm-seed">#{p.seed || i + 1}</span>
                        <span className="atm-pname">{p.username}</span>
                        <span className={`atm-pstatus atm-pstatus-${p.status}`}>{p.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          {!isSetupPhase && (
            <div className="atm-col atm-col-rounds">
              <section className="atm-section">
                <h3>{swiss ? 'Rounds' : 'Bracket'}</h3>
                {selected.status === 'in_progress' && currentRound === 0 && renderRoundStartActions(1)}
                {selected.status === 'in_progress' && currentRound > 0 && nextRound <= totalRounds && renderRoundStartActions(nextRound)}
                {selected.status === 'in_progress' && currentRound > 0 && currentRoundHasUnstarted && renderRoundStartActions(currentRound)}

                {roundsNewestFirst.map(round => {
                  const roundMatches = matches.filter(m => m.round === round);
                  if (roundMatches.length === 0 && !renderRoundStartActions(round)) return null;
                  const tc = roundSettings.find(s => s.round === round);
                  return (
                    <div key={round} className="atm-round-block">
                      <div className="atm-round-title">
                        Round {round}
                        {round === currentRound && selected.status === 'in_progress' && (
                          <span className="atm-tc-badge">Current</span>
                        )}
                        {tc && <span className="atm-tc-badge">{tc.minutes_per_player}min +{tc.increment_seconds}s</span>}
                      </div>
                      {renderRoundStartActions(round)}
                      {roundMatches.map(m => (
                        <div key={m.id} className={`atm-match atm-match-${m.status}`}>
                          <div className="atm-match-players">
                            <span className={`atm-match-player ${m.winner_id === m.player1_id ? 'atm-winner' : ''}`}>
                              {bracketPlayerLabel(m.player1_username, m.player1_id, m.status)}
                            </span>
                            <span className="atm-vs">vs</span>
                            <span className={`atm-match-player ${m.winner_id === m.player2_id ? 'atm-winner' : ''}`}>
                              {bracketPlayerLabel(m.player2_username, m.player2_id, m.status)}
                            </span>
                          </div>
                          <div className="atm-match-meta">
                            <span className={`atm-match-status atm-ms-${m.status}`}>{MATCH_STATUS_LABELS[m.status] || m.status}</span>
                            {m.player1_ready === 1 && <span className="atm-ready-dot" title="P1 ready">●</span>}
                            {m.player2_ready === 1 && <span className="atm-ready-dot" title="P2 ready">●</span>}
                            {['pending', 'ready_check', 'in_progress'].includes(m.status) && (m.player1_id || m.player2_id) && (
                              <button
                                className="atm-btn atm-btn-danger atm-btn-sm"
                                onClick={() => { setForceMatchId(m.id); setForceWinnerId(''); }}
                              >
                                Force Resolve
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </section>
            </div>
          )}
        </div>
      )}

      {/* Force resolve modal */}
      {forceMatchId !== null && (() => {
        const match = matches.find(m => m.id === forceMatchId);
        if (!match) return null;
        const options = [
          match.player1_id ? { id: match.player1_id, name: match.player1_username || 'Player 1' } : null,
          match.player2_id ? { id: match.player2_id, name: match.player2_username || 'Player 2' } : null,
        ].filter(Boolean) as { id: number; name: string }[];
        return (
          <div className="atm-modal-overlay" onClick={() => setForceMatchId(null)}>
            <div className="atm-modal" onClick={e => e.stopPropagation()}>
              <h3>Force Resolve Match</h3>
              <p>
                {swiss
                  ? 'Select a winner, record a draw (½–½), or void (both get 0 and stay in).'
                  : 'Select a winner, or void the match (both players out; their opponent in the next round gets a bye).'}
              </p>
              <select className="atm-input" value={forceWinnerId} onChange={e => setForceWinnerId(e.target.value)}>
                <option value="">
                  {swiss ? 'No winner — void (both get 0, stay in)' : 'No winner — void (both out, bracket advances)'}
                </option>
                {swiss && options.length === 2 && <option value="draw">Draw (½–½)</option>}
                {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <div className="atm-modal-actions">
                <button className="atm-btn atm-btn-danger" onClick={handleForceResolve}>Confirm</button>
                <button className="atm-btn atm-btn-secondary" onClick={() => setForceMatchId(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AdminTournamentManager;
